// assistant-overlay.ts — the assistant chat overlay's DOM (work item 64,
// PR 1; design doc helm-console/docs/assistant-agent.md). One
// implementation for BOTH clients: the Bridge home screen mounts it with
// apiBase '' and the Helm release UI with apiBase '/__console' (the
// carve-out reaches the console in both Active and Ideating modes — the
// deployments.ts "PREFIX IS LOAD-BEARING" contract).
//
// Shape (ported from the recon'd popup-agent-chat pattern, re-based onto
// this repo's idioms):
//   - a bottom-right LAUNCHER button; the PANEL is one fixed element that
//     NEVER unmounts — closed is a genie collapse into the launcher
//     (transform-origin at the launcher's center, scale+fade+radius), plus
//     `inert` + aria-hidden so hidden content is unfocusable while SSE
//     subscriptions and scroll positions survive;
//   - expand/collapse is a width swap riding the same transition;
//   - persistent tabs: the strip IS GET /api/assistant/tabs (server
//     truth); close = archive, never delete; {open, expanded, activeTabId}
//     is a localStorage echo only;
//   - transcript renders streaming text AND ACTION CARDS (one typed card
//     per agent-performed action — hard requirement, new construction);
//   - SSE per tab: replay-then-tail with the seq cursor, named event
//     'assistant', 1s→30s capped backoff, tab-list resync on reconnect.
//
// Every document-level listener and interval is removed in destroy() —
// the house teardown contract.

import {
  applyAssistantEvent,
  confirmAsk,
  CONFIRM_NO,
  CONFIRM_YES,
  isFailedOutcome,
  isStepItem,
  pendingConfirm,
  ASSISTANT_ECHO_KEY,
  buildScreenContext,
  defaultEcho,
  emptyTranscript,
  isBusyStatus,
  nextBackoff,
  parseEcho,
  sameScreenContext,
  screenAffordances,
  serializeEcho,
  streamTabIds,
  unreadCount,
  visibleTabs,
  BACKOFF_START_MS,
  CONTEXT_PUSH_MIN_MS,
} from './assistant-model.js';
import { dismiss as dismissOverlay, spotlight, startFlow as startNamedFlow } from './onboarding-api.js';
// THE HELM'S OWN TRANSCRIPT COMPONENTS (owner ruling 2026-09-08: thinking and
// tool calls render EXACTLY like the agent chat's). One module, two consumers
// — pi-view.ts is the other. Labels, icons and the collapsed summary come from
// the same pi-steps helpers the chat uses, so a tool call cannot be called one
// thing here and another there.
import { buildStepRow, buildStepsGroup, toggleStepRow, toggleStepsGroup } from './transcript-steps-dom.js';
import { composeStepsSummary, thinkingLabel, toolStep } from './pi-steps.js';
import type { PiStep } from './pi-steps.js';
import { getFlow, registeredFlowIds, registeredTargetIds } from './onboarding-flows.js';
import { iconBtn, mkIconBtn, setBtnState } from './buttons.js';
import { emptyState } from './feedback-nav.js';
import { renderMarkdown } from './md.js';
import type {
  ActionCard,
  AssistantEventRecord,
  AssistantTabRow,
  AssistantUiEcho,
  TabTranscript,
  TranscriptItem,
} from './assistant-model.js';

export interface AssistantOverlayHost {
  /** '' on the console apex; '/__console' on release subdomains. */
  apiBase: string;
  /** The signed-in console user — null refuses the mount (named
   * degradation: no console identity, no assistant). */
  user: { id: string; name?: string | null; email?: string | null } | null;
  fetchFn?: typeof fetch;
  /** Injectable for tests; default guards window.localStorage. */
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null;
  echoKey?: string;
  /** PR 3 — the screen-context seam: host-specific identifiers (surface,
   * selected release/session, visible panel ids). Merged over {hash,
   * title} basics and pushed while the panel is open; a DATA contract,
   * never pixel capture (docs/assistant-agent.md). */
  contextProvider?: () => Record<string, unknown> | null;
}

export interface AssistantOverlayHandle {
  destroy(): void;
  /** Force a tab-list refresh (drill/test seam). */
  refresh(): Promise<void>;
}

const TABLIST_POLL_MS = 30_000;

function fmtClock(ts: number): string {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

export function mountAssistantOverlay(
  hostEl: HTMLElement,
  host: AssistantOverlayHost,
): AssistantOverlayHandle | null {
  if (!host.user) return null; // rail-account idiom: null when signed out
  const fetchFn = host.fetchFn ?? fetch.bind(globalThis);
  const api = (p: string): string => `${host.apiBase}${p}`;
  const echoKey = host.echoKey ?? ASSISTANT_ECHO_KEY;
  const storage = host.storage !== undefined ? host.storage : safeStorage();

  // ---- state ----
  let tabs: AssistantTabRow[] = [];
  const transcripts = new Map<string, TabTranscript>();
  let echo: AssistantUiEcho = defaultEcho();
  try {
    echo = parseEcho(storage?.getItem(echoKey) ?? null);
  } catch { /* private mode — defaults stand */ }
  let draftActive = echo.activeTabId === null; // no server row until first send
  const streams = new Map<string, { es: EventSource; }>();
  const retries = new Map<string, { timer: ReturnType<typeof setTimeout>; ms: number }>();
  let confirmingArchive: string | null = null;
  let destroyed = false;

  const saveEcho = (): void => {
    try { storage?.setItem(echoKey, serializeEcho(echo)); } catch { /* private mode */ }
  };

  // ---- static DOM (mounted once, never rebuilt) ----
  const root = document.createElement('div');
  root.className = 'as-root';
  root.setAttribute('data-as-keep-open', '');
  root.innerHTML = `
    <button type="button" class="as-launcher" id="assistant-launcher" data-btn-role="trigger" aria-label="Assistant" aria-haspopup="dialog">
      <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><use href="#i-assistant"/></svg>
    </button>
    <section class="as-panel" data-state="closed" role="dialog" aria-label="Assistant" aria-hidden="true">
      <header class="as-head">
        <div class="as-tabs" role="tablist" aria-label="Assistant chats"></div>
        <div class="as-headbtns">
          ${iconBtn({ glyph: 'maximize-2', label: 'Expand panel', size: 'xs', cls: 'as-expandbtn' })}
          ${iconBtn({ glyph: 'x', label: 'Hide panel', size: 'xs', cls: 'as-hidebtn' })}
        </div>
      </header>
      <div class="as-transcript" aria-live="polite"></div>
      ${/* THE CONSENT AFFORDANCE (owner defect 2026-09-09). The console's
           confirm gate PROPOSES a side-effectful act and then waits for a
           user message with a higher seq — a real channel, the composer —
           but the question itself was buried as the `detail` of a collapsed
           step row inside a collapsed step group, wearing the failed-step
           treatment. The user saw a red error box and no way to say yes.
           This region puts the standing question in front of them with the
           two answers as buttons; both ANSWER THROUGH THE SAME CHANNEL the
           gate already proves consent on (send() -> a user-message event),
           so the structural proof is untouched and a model still cannot
           self-approve. */ ''}
      <div class="as-confirm" role="group" aria-label="Action awaiting your answer" hidden></div>
      <div class="as-queue"></div>
      <form class="as-composer composer" autocomplete="off">
        ${/* THE COMPOSER PRIMITIVE ("composer" / "composer-field") - the same
             two class names the Helm's transcript composer wears, and for the
             same two reasons (primitives-check's composer arm refuses either
             removal): it is a MULTI-LINE textarea rather than a standardized
             single-line fc-* field, and it takes NO standard focus ring - its
             affordance is its own border strengthening on
             .as-input:focus-visible, which the composer-block exemption in
             styles.css keeps each style's blanket :focus-visible from
             overpainting.

             The password-manager attributes are the SAME set 97fce9741 put on
             the Helm composer in August, after an operator watched iCloud
             Passwords pop a credential suggestion on focus: a bare editable
             field with no name and no autocomplete gives the heuristics
             nothing to classify, and the iCloud extension misfires on exactly
             such unattributed fields. This composer was minted (#411) WITHOUT
             them and, sitting inside a form, is the shape that misfires most
             readily. The form holds no password-shaped sibling - its only
             other control is the submit button - so the form itself stays and
             autocomplete=off on it is the second signal. */ ''}
        <textarea class="as-input composer-field" name="assistant-chat-message" rows="1" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="true" enterkeyhint="send" data-1p-ignore data-lpignore="true" data-bwignore data-form-type="other" placeholder="Ask the assistant…" aria-label="Message the assistant"></textarea>
        ${/* MULTI-ADDITIVE RESOLUTION (WF-A, pi-stack §2): the overlay had TWO
             creating affordances on screen at once — the tab strip's "+" (new
             chat) and the composer's Send. Resolved by ALTITUDE: Send is what
             this view is FOR, so it keeps the additive intent; "+" is the tab
             strip's new-tab affordance (it sits inside role="tablist" beside
             the role="tab" chips) and is stamped as such, which hands it to
             PR-3c's tabs primitive rather than leaving it a second primary. */ ''}
        ${iconBtn({ glyph: 'send', label: 'Send', intent: 'additive', ctx: 'assistant-composer', size: 'sm', type: 'submit', cls: 'as-send' })}
      </form>
    </section>`;
  hostEl.appendChild(root);

  const launcher = root.querySelector('.as-launcher') as HTMLButtonElement;
  const panel = root.querySelector('.as-panel') as HTMLElement;
  const tabstrip = root.querySelector('.as-tabs') as HTMLElement;
  const transcriptEl = root.querySelector('.as-transcript') as HTMLElement;
  const queueEl = root.querySelector('.as-queue') as HTMLElement;
  const confirmEl = root.querySelector('.as-confirm') as HTMLElement;
  const composer = root.querySelector('.as-composer') as HTMLFormElement;
  const input = root.querySelector('.as-input') as HTMLTextAreaElement;
  const expandBtn = root.querySelector('.as-expandbtn') as HTMLButtonElement;
  const hideBtn = root.querySelector('.as-hidebtn') as HTMLButtonElement;

  // ---- open/expand state (genie is pure CSS off data-state) ----
  function applyOpenState(): void {
    panel.dataset.state = echo.open ? 'open' : 'closed';
    panel.classList.toggle('as-expanded', echo.expanded);
    panel.setAttribute('aria-hidden', echo.open ? 'false' : 'true');
    (panel as HTMLElement & { inert: boolean }).inert = !echo.open;
    launcher.classList.toggle('as-launcher-hidden', echo.open);
    expandBtn.setAttribute('aria-label', echo.expanded ? 'Collapse panel' : 'Expand panel');
    if (echo.open) void markActiveRead();
  }

  function setOpen(open: boolean): void {
    if (echo.open === open) return;
    echo.open = open;
    saveEcho();
    applyOpenState();
    if (open) {
      void loadTabs();
      input.focus();
      pushContext();
    }
  }

  // ---- transcript state helpers ----
  const transcriptFor = (tabId: string): TabTranscript => {
    let t = transcripts.get(tabId);
    if (!t) {
      t = emptyTranscript();
      transcripts.set(tabId, t);
    }
    return t;
  };

  const activeTab = (): AssistantTabRow | null =>
    draftActive ? null : visibleTabs(tabs).find((t) => t.id === echo.activeTabId) ?? null;

  async function markActiveRead(): Promise<void> {
    const tab = activeTab();
    if (!tab || !tab.unread || !echo.open) return;
    tab.unread = false; // optimistic — the badge answers now
    renderTabs();
    try {
      await fetchFn(api(`/api/assistant/tabs/${encodeURIComponent(tab.id)}/read`), { method: 'POST' });
    } catch { /* the 30s list poll reconciles */ }
  }

  // ---- rendering ----
  function renderTabs(): void {
    tabstrip.textContent = '';
    const live = visibleTabs(tabs);
    for (const t of live) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'as-tab';
      btn.setAttribute('role', 'tab');
      const active = !draftActive && t.id === echo.activeTabId;
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
      btn.tabIndex = active ? 0 : -1;
      btn.classList.toggle('on', active);
      btn.dataset.tabId = t.id;
      const dot = document.createElement('span');
      dot.className = `as-dot${isBusyStatus(t.status) ? ' as-dot-busy' : t.unread ? ' as-dot-unread' : ''}`;
      const label = document.createElement('span');
      label.className = 'as-tab-label';
      label.textContent = t.title;
      btn.append(dot, label);
      if (confirmingArchive === t.id) {
        const confirm = document.createElement('span');
        confirm.className = 'as-tab-confirm';
        confirm.textContent = isBusyStatus(t.status) ? 'Archive? Run keeps going' : 'Archive?';
        btn.append(confirm);
      }
      const close = document.createElement('span');
      close.className = 'as-tab-x';
      close.setAttribute('data-as-close', t.id);
      close.setAttribute('aria-label', `Archive ${t.title}`);
      close.textContent = '×';
      btn.append(close);
      tabstrip.append(btn);
    }
    if (draftActive) {
      const draft = document.createElement('button');
      draft.type = 'button';
      draft.className = 'as-tab on as-tab-draft';
      draft.setAttribute('role', 'tab');
      draft.setAttribute('aria-selected', 'true');
      draft.tabIndex = 0;
      draft.textContent = 'New chat';
      tabstrip.append(draft);
    } else {
      // The demoted half of the multi-additive resolution above: the tab
      // strip's own new-tab control, not a second primary action.
      // PR-3c: it is the tab strip's NEW-TAB AFFORDANCE and therefore not a
      // tab. It used to carry role="tab" with no aria-selected and a default
      // tabIndex, which broke the roving invariant twice over: it advertised
      // a selectable state it can never hold, AND — because the keydown
      // handler below roves over [role="tab"] with activation-following-focus
      // — arrowing right off the last chat tab focused it and CLICKED it,
      // opening a draft the user never asked for. Dropping the role fixes
      // both: it leaves the roving set and stays reachable by Tab, which is
      // the pattern the tabs primitive renders (feedback-nav.ts, TabsSpec's
      // `newTab`).
      const plus = mkIconBtn({
        glyph: 'plus',
        label: 'New chat',
        size: 'sm',
        cls: 'as-newtab tabset-new focus-ring',
      });
      tabstrip.append(plus);
    }
    renderBadge();
  }

  let lastBadge = 0;
  function renderBadge(): void {
    const n = unreadCount(tabs);
    const existing = launcher.querySelector('.as-badge');
    if (n === 0) {
      existing?.remove();
    } else if (!existing || Number(existing.textContent) !== n) {
      existing?.remove();
      const b = document.createElement('span');
      b.className = 'as-badge'; // keyed remount replays the pop animation
      b.textContent = String(n);
      launcher.append(b);
    }
    const busy = tabs.some((t) => !t.archived && isBusyStatus(t.status));
    launcher.classList.toggle('as-launcher-live', busy && !echo.open);
    lastBadge = n;
  }

  function itemNode(item: TranscriptItem): HTMLElement {
    switch (item.kind) {
      case 'user': {
        // The Helm chat's own-bubble idiom (theme-truth): a right-aligned
        // row carrying the quiet-fill bubble plus the sent-time line
        // underneath (every message shows when it went through).
        const row = document.createElement('div');
        row.className = 'as-msg-row-user';
        const el = document.createElement('div');
        el.className = 'as-msg as-msg-user';
        el.textContent = item.text;
        row.append(el);
        if (item.ts) {
          const time = document.createElement('div');
          time.className = 'as-msg-time';
          time.textContent = fmtClock(item.ts);
          row.append(time);
        }
        return row;
      }
      case 'assistant': {
        // Owner ruling 2026-09-05: assistant replies are BUBBLES like the
        // user's — a left-aligned row, the bubble on the surface ground,
        // the .pi-md prose INSIDE (the same renderMarkdown + pi-md
        // construction pi-view.ts uses, so code blocks survive intact) —
        // never bare full-width prose. Built ONCE here, then PATCHED in
        // place by patchAssistantRow for the rest of its life.
        const row = document.createElement('div');
        row.className = 'as-msg-row-assistant';
        const el = document.createElement('div');
        el.className = 'as-msg as-msg-assistant';
        const body = renderMarkdown('', { chatBreaks: true });
        body.classList.add('pi-md', 'as-msg-body');
        el.append(body);
        row.append(el);
        patchAssistantRow(row, item, '');
        return row;
      }
      case 'action':
      case 'context':
      case 'thinking': {
        // Step items never render alone — they fold into a `.pi-steps` group
        // (renderSteps below). Reaching here would mean the block fold let a
        // step item through as a single; render it as its own one-row group
        // rather than nothing, so a fold bug is VISIBLE, not silent.
        return stepsGroupNode([item], 'orphan').group;
      }
      case 'error': {
        const el = document.createElement('div');
        el.className = 'as-errcard';
        const head = document.createElement('div');
        head.className = 'as-errcard-head';
        head.textContent = 'Assistant error';
        const body = document.createElement('div');
        body.className = 'as-errcard-body';
        body.textContent = item.message;
        el.append(head, body);
        return el;
      }
    }
  }

  /** Patch an assistant row IN PLACE to match its item (the streaming lane
   * — the Helm's pi-live fold appends to one block; this is the DOM twin).
   * `prevText` is what the row last rendered. Three outcomes, stamped on
   * the body so a probe can count them: 'appended' (the delta went onto the
   * live TEXT NODE — zero node replacements), 'rendered' (the markdown was
   * re-rendered into the SAME body element — its children changed, the
   * bubble did not), 'none'.
   *
   * WHY NOT REBUILD THE ROW (the shape before 2026-09-08): .as-msg-assistant
   * carries the as-receive-in entrance animation, and a per-delta
   * `streamRow.replaceWith(itemNode(...))` re-ran it on every token — the
   * owner-reported "text rapidly flashes while streaming". The row, bubble
   * and body elements now live as long as the item does. */
  function patchAssistantRow(
    row: HTMLElement,
    item: Extract<TranscriptItem, { kind: 'assistant' }>,
    prevText: string,
  ): 'appended' | 'rendered' | 'none' {
    row.classList.toggle('as-streaming', item.streaming);
    const bubble = row.querySelector(':scope > .as-msg-assistant') as HTMLElement;
    const body = bubble.querySelector(':scope > .as-msg-body') as HTMLElement;
    let how: 'appended' | 'rendered' | 'none' = 'none';
    if (item.text !== prevText) {
      const delta = item.text.startsWith(prevText) ? item.text.slice(prevText.length) : null;
      if (item.streaming && delta !== null && appendPlainDelta(body, prevText, delta)) {
        how = 'appended';
        body.dataset.asAppends = String(Number(body.dataset.asAppends ?? 0) + 1);
      } else {
        body.replaceChildren(...renderMarkdown(item.text, { chatBreaks: true }).childNodes);
        how = 'rendered';
        body.dataset.asRenders = String(Number(body.dataset.asRenders ?? 0) + 1);
      }
    }
    if (item.stoppedEarly && !bubble.querySelector(':scope > .as-msg-cap')) {
      const cap = document.createElement('div');
      cap.className = 'as-msg-cap';
      cap.textContent = 'stopped early';
      bubble.append(cap);
    }
    // P20 rails: the turn's measured usage line — dollars and output
    // tokens, with the basis said out loud when the figure is a floor.
    // Absent usage renders NOTHING (absent, never $0.00). It sits as a
    // FOOTER under the bubble — the user row's .as-msg-time twin,
    // mirrored left (the Helm chat's under-bubble meta idiom).
    const usage = (item.meta as { usage?: { cost_usd?: number; output?: number; basis?: string } } | null)?.usage;
    if (usage && typeof usage.cost_usd === 'number' && !row.querySelector(':scope > .as-msg-usage')) {
      const cap = document.createElement('div');
      cap.className = 'as-msg-usage';
      const floor = usage.basis === 'unmeasured' ? ' (floor — some replies carried no cost)' : '';
      cap.textContent = `$${usage.cost_usd.toFixed(4)} · ${usage.output ?? 0} tok out${floor}`;
      row.append(cap);
    }
    return how;
  }

  // ---- the step timeline (the Helm's components, not a second set) ----

  /** One transcript step item → the PiStep the shared row builder renders.
   * Thinking rows read their label from `thinkingLabel`; tool rows are built
   * by `toolStep`, so the collapsed line ("Read config.ts", "Query
   * warehouse") is composed by the SAME labeler the chat uses. */
  function stepOf(item: TranscriptItem): PiStep {
    if (item.kind === 'thinking') {
      const step: PiStep = {
        key: item.id,
        kind: 'thinking',
        icon: 'spark',
        expandable: true,
        thinkingText: item.text,
        streaming: item.streaming,
        label: '',
      };
      if (typeof item.ms === 'number') step.thinkingMs = item.ms;
      if (item.ts) step.ts = item.ts;
      step.label = thinkingLabel(step);
      return step;
    }
    if (item.kind === 'context') {
      // The screen-context read is a tool the agent ran — the transcript
      // says so as a step, never as a bespoke row (the owner's "⊕
      // bridge-home (413s old)" line).
      const step = toolStep({ id: `ctx-${item.ts}`, name: 'screen_context', args: { summary: item.summary } }, `ctx-${item.ts}`);
      if (item.ts) step.ts = item.ts;
      step.resultText = item.summary;
      return step;
    }
    const card = (item as Extract<TranscriptItem, { kind: 'action' }>).card;
    const step = toolStep(
      { id: `${card.action}-${card.ts}`, name: card.action, ...(card.target ? { args: { target: card.target } } : {}) },
      `${card.action}-${card.ts}`,
    );
    if (card.ts) step.ts = card.ts;
    // A PROPOSAL IS NOT A FAILURE. `outcome !== 'ok'` painted the console's
    // consent-pending card with the failed-step treatment (`.iserr`), which is
    // the owner's red box; the predicate lives in assistant-model so the two
    // halves of the rule cannot drift apart again.
    if (isFailedOutcome(card.outcome)) step.isError = true;
    if (card.detail) step.resultText = card.detail;
    return step;
  }

  /** The expanded detail of one step — the chat's own `.pi-think-md` prose
   * for a thought, and its own `.pi-dbox` request/response boxes for a tool
   * (the classes the shared component block styles; this surface has no pi
   * toolRun to hand the full tool-card IR). */
  function stepDetail(item: TranscriptItem): Node[] {
    if (item.kind === 'thinking') {
      const md = renderMarkdown(item.text, { chatBreaks: true });
      md.classList.add('pi-think-md');
      return [md];
    }
    const box = (title: string, body: string, err = false): HTMLElement => {
      const d = document.createElement('div');
      d.className = `pi-dbox${err ? ' err' : ''}`;
      const t = document.createElement('div');
      t.className = 'pi-dbox-t';
      t.textContent = title;
      const pre = document.createElement('pre');
      pre.textContent = body;
      d.append(t, pre);
      return d;
    };
    if (item.kind === 'context') return [box('Screen context', item.summary)];
    const card = (item as Extract<TranscriptItem, { kind: 'action' }>).card;
    const out: HTMLElement[] = [];
    if (card.target) out.push(box('Request', card.target));
    if (card.outcome === 'confirm-required') {
      // The question, titled as a question. Nothing ran, so there is no
      // "Result" to show — saying otherwise is the same lie in smaller type.
      out.push(box('Waiting on you', confirmAsk(card), false));
      return out;
    }
    out.push(box(`Result — ${card.outcome}`, card.detail ?? `(${card.outcome}, no detail)`, isFailedOutcome(card.outcome)));
    return out;
  }

  /** Expansion state, keyed exactly as the chat keys it: per step key and per
   * group key, surviving re-renders. */
  const expandedSteps = new Set<string>();
  const groupPref = new Map<string, boolean>();

  interface StepsGroupNode { group: HTMLElement; inner: HTMLElement; summary: HTMLElement }

  /** Build the `.pi-steps` group a run of step items folds into — collapsed
   * by default (the chat's settled default; `live` is what opens it there,
   * and a settled overlay run is never live). */
  function stepsGroupNode(items: TranscriptItem[], key: string): StepsGroupNode {
    const steps = items.map(stepOf);
    const g = buildStepsGroup({
      key,
      summary: composeStepsSummary(steps),
      open: groupPref.get(key) ?? false,
      live: items.some((i) => i.kind === 'thinking' && i.streaming),
    });
    for (const item of items) g.inner.append(stepRowNode(item));
    return g;
  }

  function stepRowNode(item: TranscriptItem): HTMLElement {
    const step = stepOf(item);
    const el = buildStepRow(step, {
      running: step.streaming === true,
      isErr: step.isError === true,
      expanded: expandedSteps.has(step.key),
      detailNodes: () => stepDetail(item),
    });
    el.dataset.asStepKind = item.kind;
    return el;
  }

  /**
   * Incremental transcript render. Items fold into BLOCKS first — a run of
   * step items (thinking / tool card / context read) becomes ONE collapsible
   * `.pi-steps` group, exactly as a turn segment does in the Helm's chat
   * (owner item #20: prose BREAKS the timeline, so a step after text opens a
   * new group). Everything else is its own row.
   *
   * Blocks are tracked per tab BY POSITION, never addressed by class. Settled
   * blocks are left untouched; the tail block GROWS in place (a new step row
   * is appended to the live group's inner, a delta is appended to the live
   * text node) so no entrance animation replays and no live node is
   * destroyed mid-stream.
   */
  interface RenderedBlock {
    kind: 'steps' | 'single';
    items: TranscriptItem[];
    el: HTMLElement;
    /** steps blocks: the live `.pi-steps-in` and its summary span. */
    inner?: HTMLElement;
    summary?: HTMLElement;
    /** single assistant blocks: what the row last rendered. */
    text: string;
  }

  /** Fold items into render blocks — the ONE place the segment rule lives. */
  function blocksOf(items: TranscriptItem[]): TranscriptItem[][] {
    const out: TranscriptItem[][] = [];
    for (const item of items) {
      const last = out[out.length - 1];
      if (isStepItem(item) && last && isStepItem(last[0]!)) last.push(item);
      else out.push([item]);
    }
    return out;
  }

  const rendered = new Map<string, RenderedBlock[]>();
  function renderTranscript(rebuild = false): void {
    const tab = activeTab();
    if (!tab) {
      transcriptEl.textContent = '';
      renderEmptyState();
      renderConfirm();
      renderQueue();
      return;
    }
    const t = transcriptFor(tab.id);
    let blocks = rendered.get(tab.id) ?? [];
    if (rebuild || transcriptEl.childElementCount === 0 || transcriptEl.dataset.tabId !== tab.id) {
      transcriptEl.textContent = '';
      transcriptEl.dataset.tabId = tab.id;
      blocks = [];
    }
    rendered.set(tab.id, blocks);
    // GHOST EMPTY-STATE (owner-measured 2026-09-05): items arriving over the
    // sync lane append INCREMENTALLY (no rebuild), and the placeholder was
    // never reconciled — "No messages yet." sat mid-transcript above real
    // messages, and its margin:auto made it an accidental flex spacer (the
    // whole reason fresh chats LOOKED bottom-anchored). The placeholder is
    // reconciled on every render: present iff the transcript has no items.
    if (t.items.length > 0) transcriptEl.querySelector('.as-empty')?.remove();
    // The working indicator rides the very END of the transcript — detach it
    // up front so the block math below sees ONLY content rows.
    const workingEl = transcriptEl.querySelector(':scope > .as-working') as HTMLElement | null;
    workingEl?.remove();
    const rebuilt = blocks.length === 0;
    const pinned =
      transcriptEl.scrollTop + transcriptEl.clientHeight >= transcriptEl.scrollHeight - 80;

    const want = blocksOf(t.items);
    for (let i = 0; i < want.length; i++) {
      const items = want[i]!;
      const have = blocks[i];
      if (have && !sameBlockHead(have, items)) {
        // The fold only ever appends, so a head that moved means a rebuild is
        // the honest answer for the tail — never a patch onto the wrong node.
        for (let j = blocks.length - 1; j >= i; j--) blocks[j]!.el.remove();
        blocks.length = i;
      }
      if (!blocks[i]) {
        blocks[i] = buildBlock(items);
        transcriptEl.append(blocks[i]!.el);
        continue;
      }
      patchBlock(blocks[i]!, items);
    }
    for (let j = blocks.length - 1; j >= want.length; j--) {
      blocks[j]!.el.remove();
      blocks.length = j;
    }
    if (t.items.length === 0) renderEmptyState();
    if (t.phase) {
      // The Helm's typing-bubble idiom: the row spans the transcript column,
      // the bubble inside hugs the phase text. The detached node is reused so
      // the blink animation never restarts mid-phase.
      let ind = workingEl;
      if (!ind) {
        ind = document.createElement('div');
        ind.className = 'as-working';
        const bubble = document.createElement('span');
        bubble.className = 'as-working-bubble';
        ind.append(bubble);
      }
      (ind.firstElementChild as HTMLElement).textContent =
        `✳ ${t.phase}${t.note ? ` — ${t.note}` : ''}`;
      transcriptEl.append(ind);
    }
    // A rebuild (tab switch, first hydration) always lands on the newest
    // message — deliberate anchoring, not an artifact of scroll history.
    if (pinned || rebuilt) transcriptEl.scrollTop = transcriptEl.scrollHeight;
    renderConfirm();
    renderQueue();
  }

  /** Same block, still? Identity of the FIRST item plus the kind decides — a
   * steps block grows by appending, an assistant row by accumulating text. */
  function sameBlockHead(have: RenderedBlock, items: TranscriptItem[]): boolean {
    const stepsNow = isStepItem(items[0]!);
    return have.items[0] === items[0] && have.kind === (stepsNow ? 'steps' : 'single');
  }

  function buildBlock(items: TranscriptItem[]): RenderedBlock {
    if (isStepItem(items[0]!)) {
      const key = stepGroupKey(items[0]!);
      const g = stepsGroupNode(items, key);
      return { kind: 'steps', items: [...items], el: g.group, inner: g.inner, summary: g.summary, text: '' };
    }
    const item = items[0]!;
    return {
      kind: 'single',
      items: [item],
      el: itemNode(item),
      text: item.kind === 'assistant' ? item.text : '',
    };
  }

  /** A group's key is its first step — stable across re-renders, which is
   * what keeps an expanded group open (the chat keys groups per segment for
   * the same reason). */
  function stepGroupKey(item: TranscriptItem): string {
    return `g-${stepOf(item).key}`;
  }

  function patchBlock(have: RenderedBlock, items: TranscriptItem[]): void {
    if (have.kind === 'steps') {
      // Rows already rendered are patched only when their item MOVED (a
      // thought settling from "Thinking…" to "Thought for 2 sec"); a settled
      // row is left alone, so nothing re-animates.
      const rows = [...have.inner!.children] as HTMLElement[];
      for (let i = 0; i < have.items.length && i < items.length; i++) {
        if (have.items[i] === items[i] && !stepRowStale(rows[i], items[i]!)) continue;
        const fresh = stepRowNode(items[i]!);
        rows[i]?.replaceWith(fresh);
        have.items[i] = items[i]!;
      }
      for (let i = have.items.length; i < items.length; i++) {
        have.inner!.append(stepRowNode(items[i]!));
        have.items.push(items[i]!);
      }
      const steps = items.map(stepOf);
      have.summary!.textContent = composeStepsSummary(steps) || 'Working…';
      have.el.classList.toggle('live', items.some((i) => i.kind === 'thinking' && i.streaming));
      return;
    }
    const item = items[0]!;
    if (item.kind !== 'assistant') return;
    patchAssistantRow(have.el, item, have.text);
    have.text = item.text;
  }

  /** Has the rendered row fallen behind its item? Only the two states a step
   * row can change into: running→settled, and a label that recomposed. */
  function stepRowStale(el: HTMLElement | undefined, item: TranscriptItem): boolean {
    if (!el) return true;
    const step = stepOf(item);
    const runningNow = el.classList.contains('running');
    const labelEl = el.querySelector('.pi-step-label');
    return runningNow !== (step.streaming === true) || labelEl?.textContent !== step.label;
  }

  function renderEmptyState(): void {
    if (transcriptEl.querySelector('.as-empty')) return;
    // THE empty-state primitive (#1655 slice D). `as-empty` rides the
    // primitive's `cls` seat because three readers key on it: the idempotence
    // guard above, the reconcile removal in render(), and the transcript's
    // `:first-child:not(.as-empty)` bottom-anchor rule.
    transcriptEl.insertAdjacentHTML('beforeend', emptyState({
      glyph: 'message-square',
      title: draftActive
        ? 'Ask about the app, your releases, or what to do next.'
        : 'No messages yet.',
      cls: 'as-empty',
    }));
  }

  /** The standing question, if the agent asked one and the user has not yet
   * answered. Rebuilt from the transcript on every render — there is no
   * separate client-side pending state to fall out of sync, and a reload or
   * a tab switch shows the same question the transcript holds. */
  function renderConfirm(): void {
    const tab = activeTab();
    const card = tab ? pendingConfirm(transcriptFor(tab.id).items) : null;
    if (!card) {
      confirmEl.textContent = '';
      confirmEl.hidden = true;
      return;
    }
    // Rebuilding the node on every render would steal focus from a button
    // mid-keyboard-answer; the question is keyed, and an unchanged key is
    // left alone.
    const key = card.fp ?? `${card.action}-${card.ts}`;
    if (confirmEl.dataset.key === key) return;
    confirmEl.dataset.key = key;
    confirmEl.textContent = '';
    confirmEl.hidden = false;
    const q = document.createElement('div');
    q.className = 'as-confirm-q';
    q.textContent = confirmAsk(card);
    const note = document.createElement('div');
    note.className = 'as-confirm-note';
    note.textContent = 'Nothing has happened yet.';
    const row = document.createElement('div');
    row.className = 'as-confirm-btns';
    const answer = (text: string): void => {
      // Disable BOTH buttons before the await: a double-click used to send
      // two consent turns, and the second one confirms the NEXT proposal.
      for (const b of [...row.children] as HTMLButtonElement[]) setBtnState(b, 'disabled');
      void send(text);
    };
    // ON THE PRIMITIVE, exactly as confirm.ts's hosted dialog is: the verb
    // that used to be the button's text is the whole tooltip and accessible
    // name, and the intent ramp paints what the bespoke .as-confirm-btn rules
    // used to. The first cut set `data-btn` WITHOUT `.ibtn`, which
    // buttons-check names for what it is — "an intent without the primitive is
    // a claim, not a button" — and refused both sites by file:line.
    const yes = mkIconBtn({
      glyph: 'check',
      label: 'Yes, do it',
      intent: 'additive',
      // Per STANDING QUESTION, not per surface. The composer's Send owns
      // 'assistant-composer'; this is a different view context that exists
      // only while a question is standing, so the one-additive-per-context
      // rule stays true for both.
      ctx: 'assistant-overlay-confirm',
      // The inline arm-then-fire tier is deliberately OFF: this IS the
      // consent gate, and arming a second question inside it would be the
      // double-confirm the one-primitive ruling exists to kill.
      noConfirm: 'this IS the consent gate — the question is the card above it',
      attrs: { 'data-act': 'yes' },
    });
    yes.addEventListener('click', () => answer(CONFIRM_YES));
    const no = mkIconBtn({ glyph: 'x', label: 'No', attrs: { 'data-act': 'no' } });
    no.addEventListener('click', () => answer(CONFIRM_NO));
    row.append(yes, no);
    confirmEl.append(q, note, row);
  }

  function renderQueue(): void {
    queueEl.textContent = '';
    const tab = activeTab();
    if (!tab) return;
    const t = transcriptFor(tab.id);
    for (const q of t.queue) {
      const el = document.createElement('div');
      el.className = 'as-queued';
      el.textContent = q.text;
      queueEl.append(el);
    }
  }

  // ---- transport ----
  async function loadTabs(): Promise<void> {
    try {
      const res = await fetchFn(api('/api/assistant/tabs'));
      if (!res.ok) return;
      const body = (await res.json()) as { tabs?: AssistantTabRow[] };
      tabs = Array.isArray(body.tabs) ? body.tabs : [];
    } catch {
      return; // offline — the poll retries
    }
    if (destroyed) return;
    const live = visibleTabs(tabs);
    if (!draftActive && !live.some((t) => t.id === echo.activeTabId)) {
      echo.activeTabId = live[0]?.id ?? null;
      draftActive = echo.activeTabId === null;
      saveEcho();
    }
    renderTabs();
    renderTranscript();
    ensureStreams();
  }

  function ensureStreams(): void {
    if (typeof EventSource === 'undefined') return; // headless probes
    const want = new Set(streamTabIds(tabs, draftActive ? null : echo.activeTabId));
    for (const [id, s] of streams) {
      if (!want.has(id)) {
        s.es.close();
        streams.delete(id);
      }
    }
    for (const id of want) {
      if (!streams.has(id) && !retries.has(id)) openStream(id);
    }
  }

  function openStream(tabId: string): void {
    if (destroyed || typeof EventSource === 'undefined') return;
    const t = transcriptFor(tabId);
    const es = new EventSource(
      api(`/api/assistant/tabs/${encodeURIComponent(tabId)}/stream?cursor=${t.cursor}`),
    );
    streams.set(tabId, { es });
    let backoff = BACKOFF_START_MS;
    es.addEventListener('open', () => {
      backoff = BACKOFF_START_MS;
      t.loaded = true;
    });
    es.addEventListener('assistant', (e) => {
      try {
        const ev = JSON.parse((e as MessageEvent).data) as AssistantEventRecord;
        if (ev.kind === 'overlay-cmd') handleOverlayCmd(ev); // acts in-page; fold treats it as unknown
        const change = applyAssistantEvent(t, ev);
        if (change === 'items' || change === 'phase') {
          if (!draftActive && echo.activeTabId === tabId) renderTranscript();
          // done/error move unread + status server-side — resync the strip.
          if (ev.kind === 'done' || ev.kind === 'error' || ev.kind === 'status') void loadTabs();
        } else if (change === 'queue') {
          renderQueue();
        }
      } catch { /* a malformed frame must not kill the stream */ }
    });
    es.addEventListener('error', () => {
      es.close();
      streams.delete(tabId);
      if (destroyed) return;
      const ms = backoff;
      backoff = nextBackoff(backoff);
      retries.set(tabId, {
        ms,
        timer: setTimeout(() => {
          retries.delete(tabId);
          // Resync-on-reconnect: the list may have moved while dark.
          void loadTabs();
        }, ms),
      });
    });
  }

  async function send(text: string): Promise<void> {
    const body = { tab_id: draftActive ? null : echo.activeTabId, text };
    let res: Response;
    try {
      res = await fetchFn(api('/api/assistant/messages'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
    } catch {
      flashComposer('offline — not sent');
      return;
    }
    const out = (await res.json().catch(() => ({}))) as { tab_id?: string; error?: string };
    if (!res.ok) {
      flashComposer(out.error ?? `send failed (${res.status})`);
      return;
    }
    input.value = '';
    if (out.tab_id) {
      draftActive = false;
      echo.activeTabId = out.tab_id;
      saveEcho();
      pushContext(); // the run reads the freshest snapshot at tool time
    }
    await loadTabs();
  }

  function flashComposer(msg: string): void {
    let note = composer.querySelector('.as-composer-note') as HTMLElement | null;
    if (!note) {
      note = document.createElement('div');
      note.className = 'as-composer-note';
      composer.append(note);
    }
    note.textContent = msg;
    setTimeout(() => note?.remove(), 4000);
  }

  async function archiveTab(tabId: string): Promise<void> {
    confirmingArchive = null;
    try {
      await fetchFn(api(`/api/assistant/tabs/${encodeURIComponent(tabId)}/archive`), { method: 'POST' });
    } catch { /* poll reconciles */ }
    if (echo.activeTabId === tabId) {
      const rest = visibleTabs(tabs).filter((t) => t.id !== tabId);
      echo.activeTabId = rest[0]?.id ?? null;
      draftActive = echo.activeTabId === null;
      saveEcho();
    }
    await loadTabs();
  }

  // ---- overlay commands (PR 3, live since #403's spotlight engine) ----
  // The agent's overlay_spotlight / overlay_dismiss tools append
  // `overlay-cmd` events; this client invokes the shared onboarding engine
  // in-page. FRESHNESS GUARD: a reconnect REPLAYS history from the cursor,
  // and re-spotlighting a command from minutes ago would flash stale
  // guidance — only commands younger than OVERLAY_CMD_FRESH_MS act.
  const OVERLAY_CMD_FRESH_MS = 15_000;
  function handleOverlayCmd(ev: AssistantEventRecord): void {
    const age = Date.now() - (Date.parse(ev.ts) || 0);
    if (age > OVERLAY_CMD_FRESH_MS) return; // replayed history, not a command
    const p = ev.payload as { cmd?: string; target_id?: string; text?: string; flow?: string };
    if (p.cmd === 'dismiss') {
      dismissOverlay();
      return;
    }
    if (p.cmd === 'spotlight' && typeof p.target_id === 'string' && p.target_id) {
      // The PROGRAMMATIC seam (onboarding-api), not a hand-rolled FlowDef:
      // text-in-never-markup, no persistence, one overlay at a time — the
      // properties that seam exists to guarantee for an agent-supplied
      // string. A refusal (another overlay up) returns null and is logged.
      const h = spotlight(p.target_id, {
        title: 'Assistant',
        copy: typeof p.text === 'string' ? p.text : '',
      });
      if (!h) console.info(`[assistant] spotlight #${p.target_id}: refused (an overlay is already up, or the target is absent)`);
      return;
    }
    if (p.cmd === 'start_flow' && typeof p.flow === 'string' && p.flow) {
      // A registered flow by name. An unknown id must be a NAMED miss, not a
      // silent no-op — the agent is told what exists through screen_context.
      if (!getFlow(p.flow)) {
        console.info(`[assistant] start_flow '${p.flow}': unknown flow id — registered here: [${registeredFlowIds().join(', ')}]`);
        return;
      }
      if (!startNamedFlow(p.flow)) console.info(`[assistant] start_flow '${p.flow}': refused (an overlay is already up)`);
    }
  }

  // ---- screen context push (PR 3) ----
  // While the panel is OPEN, the overlay keeps the console's per-tab
  // snapshot current: on open, tab switch, hash change, and send —
  // throttled, and only when the snapshot actually changed. Closed panel
  // = no pushes (documented: a run mid-flight reads the last snapshot).
  let lastContext: Record<string, unknown> | null = null;
  let lastContextPush = 0;
  let contextTimer: ReturnType<typeof setTimeout> | null = null;
  function pushContext(): void {
    if (!echo.open || draftActive || !echo.activeTabId) return;
    // The AFFORDANCES ride the same snapshot: the ids the agent may
    // spotlight (the onboarding registry's own curated controls, filtered to
    // what is really on this page) and the flows it may run. Without them
    // overlay_spotlight/overlay_start_flow are tools the agent has no way to
    // aim — which is how "it can't see my screen" reads to a user.
    const affordances = screenAffordances(
      (id) => document.getElementById(id) !== null,
      registeredTargetIds(),
      registeredFlowIds(),
    );
    const ctx = buildScreenContext(
      { hash: location.hash, title: document.title },
      { ...(host.contextProvider ? host.contextProvider() : null), ...affordances },
    );
    if (sameScreenContext(ctx, lastContext)) return;
    const wait = CONTEXT_PUSH_MIN_MS - (Date.now() - lastContextPush);
    if (wait > 0) {
      if (!contextTimer) contextTimer = setTimeout(() => {
        contextTimer = null;
        pushContext();
      }, wait);
      return;
    }
    lastContext = ctx;
    lastContextPush = Date.now();
    void fetchFn(api(`/api/assistant/tabs/${encodeURIComponent(echo.activeTabId)}/context`), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ context: ctx }),
    }).catch(() => {
      lastContext = null; // failed push must not suppress the retry
    });
  }
  const onHashChange = (): void => pushContext();
  window.addEventListener('hashchange', onHashChange);

  // ---- events ----
  launcher.addEventListener('click', () => setOpen(true));
  hideBtn.addEventListener('click', () => setOpen(false));
  expandBtn.addEventListener('click', () => {
    echo.expanded = !echo.expanded;
    saveEcho();
    applyOpenState();
  });

  tabstrip.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const closeId = target.getAttribute('data-as-close');
    if (closeId) {
      const tab = tabs.find((t) => t.id === closeId);
      if (tab && isBusyStatus(tab.status) && confirmingArchive !== closeId) {
        confirmingArchive = closeId; // second click confirms — the run keeps going
        renderTabs();
      } else {
        void archiveTab(closeId);
      }
      return;
    }
    if (target.closest('.as-newtab')) {
      draftActive = true;
      echo.activeTabId = null;
      saveEcho();
      renderTabs();
      renderTranscript(true);
      input.focus();
      return;
    }
    const btn = target.closest('.as-tab') as HTMLElement | null;
    if (btn?.dataset.tabId) {
      draftActive = false;
      echo.activeTabId = btn.dataset.tabId;
      confirmingArchive = null;
      saveEcho();
      renderTabs();
      renderTranscript(true);
      ensureStreams();
      void markActiveRead();
      pushContext();
    }
  });

  // Roving tabindex + arrows (the settings-tablist idiom: activation
  // follows focus, Home/End jump).
  tabstrip.addEventListener('keydown', (e) => {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(e.key)) return;
    const all = [...tabstrip.querySelectorAll<HTMLElement>('[role="tab"]')];
    const cur = all.findIndex((el) => el === document.activeElement);
    if (cur < 0 || all.length === 0) return;
    e.preventDefault();
    const next =
      e.key === 'Home' ? 0
      : e.key === 'End' ? all.length - 1
      : (cur + (e.key === 'ArrowRight' ? 1 : all.length - 1)) % all.length;
    all[next]!.focus();
    all[next]!.click();
  });

  // Step expand/collapse — the SAME delegation the chat uses (pi-view.ts:
  // one listener on the scroll root, keyed off data-steps-toggle /
  // data-step-toggle), through the SAME toggles. Nodes come and go; the
  // listener does not.
  transcriptEl.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    const grp = target.closest<HTMLElement>('[data-steps-toggle]');
    if (grp?.dataset.stepsToggle) {
      const open = toggleStepsGroup(transcriptEl, grp.dataset.stepsToggle);
      if (open !== null) groupPref.set(grp.dataset.stepsToggle, open);
      return;
    }
    const step = target.closest<HTMLElement>('[data-step-toggle]');
    if (step?.dataset.stepToggle) {
      const key = step.dataset.stepToggle;
      const open = toggleStepRow(transcriptEl, key, () => {
        const item = itemForStepKey(key);
        return item ? stepDetail(item) : [];
      });
      if (open === true) expandedSteps.add(key);
      else if (open === false) expandedSteps.delete(key);
    }
  });

  /** The item behind a step key — the detail builder's lookup (the chat's
   * stepIndex twin, derived rather than cached: the overlay's transcripts are
   * a chat's worth of rows, not a session's). */
  function itemForStepKey(key: string): TranscriptItem | null {
    const tab = activeTab();
    if (!tab) return null;
    for (const item of transcriptFor(tab.id).items) {
      if (isStepItem(item) && stepOf(item).key === key) return item;
    }
    return null;
  }

  composer.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (text) void send(text);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const text = input.value.trim();
      if (text) void send(text);
    }
  });

  // Click-outside hides WITHOUT swallowing the click (capture-phase, no
  // preventDefault — clicking a link both navigates and hides). Carve-outs:
  // the overlay itself and anything opting in via data-as-keep-open.
  const onDocPointerDown = (e: PointerEvent): void => {
    if (!echo.open) return;
    const target = e.target as HTMLElement | null;
    if (!target) return;
    if (target.closest('.as-root,[data-as-keep-open]')) return;
    setOpen(false);
  };
  document.addEventListener('pointerdown', onDocPointerDown, true);

  const onDocKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && echo.open && panel.contains(document.activeElement)) setOpen(false);
  };
  document.addEventListener('keydown', onDocKeydown);

  const poll = setInterval(() => {
    if (echo.open || streams.size > 0) void loadTabs();
  }, TABLIST_POLL_MS);

  // ---- boot ----
  applyOpenState();
  renderTabs();
  renderTranscript();
  // A page load with the panel ALREADY open (the echo persists across
  // refreshes and route changes — that is the whole point of it) never went
  // through setOpen, so the console held NO snapshot for the tab and
  // screen_context answered "no screen context received yet". Measured in a
  // real browser 2026-09-07: the first push only happened on a tab switch,
  // a hash change or a send. Push once at boot, after the tab list resolves
  // the active tab the snapshot belongs to.
  void loadTabs().then(() => {
    if (echo.open) pushContext();
  });

  return {
    refresh: () => loadTabs(),
    destroy(): void {
      destroyed = true;
      clearInterval(poll);
      for (const [, s] of streams) s.es.close();
      streams.clear();
      for (const [, r] of retries) clearTimeout(r.timer);
      retries.clear();
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onDocKeydown);
      window.removeEventListener('hashchange', onHashChange);
      if (contextTimer) clearTimeout(contextTimer);
      root.remove();
    },
  };
}

/** Characters that can open or close markdown syntax, autolink, or break a
 * line. A delta carrying any of them goes through the full renderer; a
 * delta of bare prose is appended to the live text node instead. */
const MD_SIGNIFICANT = /[\\`*_#[\]()<>|~!&:\/@\n\r-]/;

/** The streaming fast path: append `delta` to the body's LAST text node
 * when that is provably equivalent to re-rendering — bare prose, appended
 * mid-line (a line start could open a heading, list or fence), onto a text
 * node that is the last child of a paragraph or list item (never inside
 * an emphasis/code/link span, where it would inherit the wrap). Returns
 * false to hand the delta to the full renderer. Exported for the smoke. */
export function appendPlainDelta(body: HTMLElement, prevText: string, delta: string): boolean {
  if (!delta || !prevText || MD_SIGNIFICANT.test(delta)) return false;
  if (/(^|\n)[ \t]*$/.test(prevText)) return false; // at a line start
  let leaf: Node = body;
  while (leaf.lastChild) leaf = leaf.lastChild;
  if (leaf.nodeType !== 3) return false; // Node.TEXT_NODE — a <br> or an empty element
  const parent = leaf.parentNode as HTMLElement | null;
  if (!parent || (parent.tagName !== 'P' && parent.tagName !== 'LI')) return false;
  (leaf as Text).appendData(delta);
  return true;
}

function safeStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    const s = window.localStorage;
    s.getItem('__as_probe');
    return s;
  } catch {
    return null;
  }
}
